
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import { useGroupVoteAuthGate } from '@/utils/groupVoteAuthGate';
import GroupVoteAuthModal from '@/components/groupVote/GroupVoteAuthModal';

const FEATURES = [
  'Invite tes potes par lien ou par SMS',
  'Vote en 30 secondes, même sans compte',
  'Confirmation en 1 clic quand tout le monde a voté',
];

const GroupVoteShowcase = () => {
  const shouldReduce = useReducedMotion();
  const navigate = useNavigate();
  const { requireAuth, showModal, closeModal } = useGroupVoteAuthGate();

  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-10 sm:pt-20">
      <div
        className="relative rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-8 lg:gap-16 items-center px-5 sm:px-12 lg:px-16 py-10 sm:py-16 lg:py-[72px] text-white"
        style={{ background: 'linear-gradient(135deg, #1A1A2E 0%, #2a2a48 70%, #004E89 100%)' }}
      >
        {/* Decorative blobs */}
        <div className="absolute w-96 h-96 rounded-full pointer-events-none" style={{ background: 'rgba(255,107,53,.15)', top: '-150px', right: '-140px' }} />
        <div className="absolute w-72 h-72 rounded-full pointer-events-none" style={{ background: 'rgba(0,201,167,.12)', bottom: '-100px', left: '-100px' }} />

        {/* Left: copy */}
        <div className="relative z-10">
          <span className="inline-block bg-white/10 text-[#FFC0A6] rounded-pill px-3.5 py-1.5 text-[13px] font-semibold mb-5">
            Notre signature
          </span>
          <h2
            className="font-heading font-extrabold leading-[1.1] tracking-tight mb-4 text-white"
            style={{ fontSize: 'clamp(30px, 3.6vw, 44px)', letterSpacing: '-0.02em' }}
          >
            Plus jamais de débat WhatsApp{' '}
            <em className="not-italic text-brand-primary">pendant 3 jours</em>.
          </h2>
          <p className="text-[17px] leading-relaxed text-white/82 mb-7 max-w-[46ch]">
            Tu envoies le lien à ta bande, chacun coche ses dispos, on retient le créneau gagnant. C'est tout. Pas de tableur, pas de doodle, pas de drama.
          </p>

          <div className="flex flex-col gap-3.5 mb-8">
            {FEATURES.map((feat) => (
              <div key={feat} className="flex items-start gap-3 text-[15px] text-white/88">
                <span className="w-6 h-6 rounded-full bg-brand-success flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">✓</span>
                {feat}
              </div>
            ))}
          </div>

          <button
            onClick={() => requireAuth(() => {
              navigate('/search');
              toast.info('Choisis une activité pour lancer ton vote de groupe 🗳');
            })}
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-600 text-white font-semibold px-6 py-3.5 rounded-lg shadow-brand-primary transition-colors duration-200 text-[15px]"
          >
            Lancer un vote
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Right: phone mockup */}
        <div className="relative z-10 flex justify-center">
          <div className="relative" style={{ maxWidth: 320, width: '100%' }}>
            {/* Phone shell */}
            <div
              className="relative w-full"
              style={{
                aspectRatio: '9/19',
                background: '#1A1A2E',
                borderRadius: 42,
                padding: 12,
                boxShadow: '0 30px 80px rgba(0,0,0,.5), 0 0 0 2px rgba(255,255,255,.06) inset',
              }}
            >
              {/* Notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-brand-dark rounded-xl z-10" />

              {/* Screen */}
              <div className="relative h-full bg-white rounded-3xl overflow-hidden">
                {/* Header */}
                <div className="px-4 pt-10 pb-3 border-b border-brand-border">
                  <h4 className="font-heading font-bold text-[15px] text-brand-text mb-0.5">Bowling Star Paris 12</h4>
                  <div className="text-[11px] text-brand-text-muted">4 participants · expire dans 2h17min</div>
                </div>

                {/* Progress */}
                <div className="px-4 py-2.5 bg-brand-success/8 flex items-center gap-2 text-[11px] text-emerald-700 font-semibold">
                  <span>3 sur 4 ont voté</span>
                  <div className="flex-1 h-1 bg-brand-success/20 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-brand-success rounded-full" />
                  </div>
                  <span>75%</span>
                </div>

                {/* Days */}
                <div className="flex gap-1.5 px-4 py-3 overflow-hidden">
                  {['Sam 14', 'Dim 15', 'Lun 16', 'Mar 17'].map((d, i) => (
                    <span
                      key={d}
                      className={`px-2.5 py-1.5 rounded-full border text-[11px] font-semibold flex-shrink-0 ${
                        i === 0
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : i === 1
                          ? 'bg-brand-primary-50 border-brand-primary/40 text-brand-primary'
                          : 'bg-white text-brand-text border-brand-border'
                      }`}
                    >
                      {d}
                    </span>
                  ))}
                </div>

                {/* Slots */}
                <div className="grid grid-cols-2 gap-2 px-4 pb-2">
                  {[
                    { t: '17:00', note: 'Aucun vote', voted: false, mine: false },
                    { t: '18:00', note: '✓ 2 votes', voted: false, mine: false },
                    { t: '19:00', note: '✓ 4 votes (toi)', voted: true, mine: true },
                    { t: '20:00', note: '✓ 1 vote', voted: false, mine: false },
                    { t: '20:30', note: '✓ 3 votes (toi)', voted: true, mine: true },
                    { t: '21:00', note: 'Aucun vote', voted: false, mine: false },
                  ].map(({ t, note, voted, mine }) => (
                    <div
                      key={t}
                      className={`rounded-[10px] p-2.5 ${voted ? 'border-2 border-brand-primary bg-brand-primary/4' : 'border border-brand-border bg-white'}`}
                    >
                      <div className="font-heading font-bold text-[14px] text-brand-text">{t}</div>
                      <div className="text-[10px] text-brand-text-muted">1h30</div>
                      <div className={`text-[10px] font-bold mt-1 ${voted || mine ? 'text-brand-primary' : 'text-brand-text-subtle'}`}>{note}</div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mx-4 mb-4 py-3 bg-brand-primary rounded-[10px] text-center text-white font-semibold text-[13px] shadow-brand-primary">
                  Voter
                </div>
              </div>
            </div>

            {/* Floating badges */}
            <div
              className="absolute bg-white rounded-pill px-3 py-2 flex items-center gap-2 text-[12px] font-semibold text-brand-text shadow-brand-lg"
              style={{ top: '28%', right: -42, transform: 'rotate(6deg)' }}
            >
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 border-2 border-white flex-shrink-0" />
              Léa a voté&nbsp;!
            </div>
            <div
              className="absolute bg-white rounded-pill px-3 py-2 flex items-center gap-2 text-[12px] font-semibold text-brand-text shadow-brand-lg"
              style={{ bottom: '22%', left: -54, transform: 'rotate(-5deg)' }}
            >
              <span className="w-4.5 h-4.5 rounded-full bg-brand-success text-white flex items-center justify-center text-[11px] font-black">✓</span>
              Créneau bloqué
            </div>
          </div>
        </div>
      </div>
      <GroupVoteAuthModal isOpen={showModal} onClose={closeModal} />
    </section>
  );
};

export default GroupVoteShowcase;
